import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getDeviceFirmwareHistory, getDeviceBySerial, getDeviceAuditLog, getDeviceAppLog, getDeviceOtaLogs } from "../../lib/api";
import OtaLogTabPanel from "./OtaLogTabPanel";
import { PageLoader } from "../../components/ui/spinner";
import { formatLastSync } from "../../lib/freshness";
import { formatSegment } from "../../lib/segment";
import { jsPDF } from "jspdf";
import { useAuth } from "../../contexts/AuthContext";
import { AccessDeniedDialog } from "../../components/ui/access-denied-dialog";
import { StaleBadge } from "../../components/ui/stale-badge";

const DOT_COLOR = {
  SUCCESS:    "bg-emerald-500",
  REGISTERED: "bg-[#08148C]",
  ROLLBACK:   "bg-red-500",
};

// Pill styling per firmware-history outcome; unknown outcomes fall back to grey.
const OUTCOME_PILL = {
  SUCCESS:    "bg-emerald-50 text-emerald-600 border-emerald-300",
  REGISTERED: "bg-blue-50 text-[#08148C] border-blue-200",
  ROLLBACK:   "bg-amber-50 text-amber-600 border-amber-200",
  FAIL:       "bg-red-50 text-red-600 border-red-200",
};
const OUTCOME_PILL_DEFAULT = "bg-slate-50 text-slate-500 border-slate-200";

// The tabs in the right-hand panel, in the order they are shown. Device Info is
// the device as a whole; the other three are all about ONE selected update.
const TABS = [
  { key: "metadata", label: "Device Info" },
  { key: "applog",   label: "App Log" },
  { key: "audit",    label: "Audit Trail" },
  { key: "otalog",   label: "OTA Log" },
];

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm font-semibold pb-2.5 border-b-2 transition-colors ${
        active ? "border-[#08148C] text-[#08148C]" : "border-transparent text-slate-400 hover:text-slate-600"
      }`}
    >
      {label}
    </button>
  );
}

// Which tab a FW History click lands on. Selecting an update from the OTA Log
// tab keeps the reader there; every other entry point lands on the audit trail,
// as it always has.
function tabAfterHistoryClick(currentTab) {
  return currentTab === "otalog" ? "otalog" : "audit";
}

// Text styling for a metadata field, driven by the flags on the field descriptor.
function metaTextClass(item) {
  if (item.mono) return "font-mono text-slate-700";
  if (item.fw) return "font-mono text-[#08148C]";
  return "text-slate-800";
}

// Same idea for the log-detail field grid, which has an extra "uppercase" flag.
function logFieldTextClass(item) {
  if (item.mono) return "font-mono text-sm text-slate-500";
  if (item.fw) return "font-mono font-semibold text-[#08148C]";
  if (item.uppercase) return "text-slate-600 uppercase tracking-wide";
  return "text-slate-700";
}

// True when the portal is being served from a developer machine, in which case
// S3 log URLs are routed through the local dev-server proxy instead of hitting
// the bucket directly (CORS). Matched by prefix so no IP literal is hardcoded.
function isLocalDevHost() {
  const host = window.location.hostname;
  return host === "localhost" || host.startsWith("127.");
}

function formatLogDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short", timeZone: "UTC" });
}

function writePdfHeader(doc, { title, subtitle, margin, pageWidth }) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(8, 20, 140);
  doc.text(title, margin, margin);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(subtitle, margin, margin + 16);
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, margin + 24, pageWidth - margin, margin + 24);
}

// Owns the wrap + page-break loop. Lives here rather than inline so the call
// sites stay shallow (S2004: no more than 5 levels of nested functions).
function writePdfBody(doc, text, { margin, pageWidth, lineHeight }) {
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(30, 30, 30);
  const maxWidth = pageWidth - margin * 2;
  const pageBottom = doc.internal.pageSize.getHeight() - margin;
  const lines = text
    .split("\n")
    .flatMap((line) => doc.splitTextToSize(line || " ", maxWidth));

  let y = margin + 38;
  for (const line of lines) {
    if (y + lineHeight > pageBottom) {
      doc.addPage();
      y = margin;
    }
    doc.text(line, margin, y);
    y += lineHeight;
  }
}

// Fetches a device log from S3 and saves it as a PDF. Routed through the local
// dev-server proxy when running on a developer machine (CORS).
async function downloadLogAsPdf({ downloadUrl, title, subtitle, fileName }) {
  if (!downloadUrl) return;
  const fetchUrl = isLocalDevHost()
    ? downloadUrl.replace(
        "https://emed-device-logs.s3.amazonaws.com",
        window.location.origin + "/s3-logs"
      )
    : downloadUrl;

  const response = await fetch(fetchUrl);
  if (!response.ok) throw new Error(response.status);
  const text = await response.text();

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const layout = { margin: 40, pageWidth: doc.internal.pageSize.getWidth(), lineHeight: 13 };
  writePdfHeader(doc, { title, subtitle, ...layout });
  writePdfBody(doc, text, layout);
  doc.save(fileName);
}

export default function DeviceDetailPage() {
  const { serial } = useParams();
  const navigate = useNavigate();
  // Read-only roles (Nurse/Patient) can view device logs but never download them (003 Role Gate).
  const { canAccess } = useAuth();
  const canDownload = canAccess("FleetAdmin", "ReleaseManager", "SupportEngineer");
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "metadata";
  const setTab = (t) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("tab", t); return p; });
  const initialFwYear = useRef(String(new Date().getFullYear()));
  const initialFwQuarter = useRef(String(Math.ceil((new Date().getMonth() + 1) / 3)));
  const [fwHistory, setFwHistory] = useState(null);
  const [fwHistoryError, setFwHistoryError] = useState(null);
  const [fwYear, setFwYear] = useState(initialFwYear.current);
  const [fwQuarter, setFwQuarter] = useState(initialFwQuarter.current);
  const [fwFilterLoading, setFwFilterLoading] = useState(false);
  const [apiDevice, setApiDevice] = useState(null);
  // True when the fleet list loaded but holds no device with this serial.
  // DISC-026: such a serial used to render MOCK_DEVICES[0] - a fabricated device
  // - with no sign that the serial was not real.
  const [deviceNotFound, setDeviceNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [appLogData, setAppLogData] = useState(null);
  const [appLogLoading, setAppLogLoading] = useState(false);
  const [appLogError, setAppLogError] = useState(null);
  // The logs the device filed before and after the selected update.
  const [otaLogs, setOtaLogs] = useState(null);
  const [otaLogsLoading, setOtaLogsLoading] = useState(false);
  const [otaLogsError, setOtaLogsError] = useState(null);

  // No mock fallback: a serial the service does not know is shown as not found,
  // never as a fabricated device record (DISC-015, TP-FMP-04-TS-01-TC-0013).
  const device = apiDevice;

  useEffect(() => {
    if (!serial) return;
    setFwHistory(null);
    setFwHistoryError(null);
    setDeviceNotFound(false);
    setLoading(true);
    Promise.all([
      getDeviceFirmwareHistory(serial, {
        year: initialFwYear.current || undefined,
        quarter: initialFwQuarter.current || undefined,
      })
        .then((records) => setFwHistory(records))
        .catch((err) => {
          console.error("[DeviceDetail] FW history fetch failed:", err);
          setFwHistoryError(err.message || "Failed to load firmware history");
        }),
      // A direct lookup, not the fleet list: the list is paginated, so a real
      // device past its first page would otherwise read as "not found".
      getDeviceBySerial(serial)
        .then(({ device, notFound }) => {
          if (device) setApiDevice(device);
          else if (notFound) setDeviceNotFound(true);
        })
        .catch((err) => console.error("[DeviceDetail] Device fetch failed:", err)),
    ]).finally(() => setLoading(false));
  }, [serial, initialFwQuarter, initialFwYear]);
  function handleHistoryClick(h, opts) {
    if (!h.update_id) return;
    setSelectedHistory(h);
    setAuditLog(null);
    setAuditError(null);
    setAuditLoading(true);
    setAppLogData(null);
    setAppLogError(null);
    setAppLogLoading(true);
    setOtaLogs(null);
    setOtaLogsError(null);
    setOtaLogsLoading(true);
    setTab(tabAfterHistoryClick(tab));
    (opts ? getDeviceAuditLog(serial, h.update_id, opts) : getDeviceAuditLog(serial, h.update_id))
      .then((records) => setAuditLog(records))
      .catch((err) => setAuditError(err.message || "Failed to load audit log"))
      .finally(() => setAuditLoading(false));
    getDeviceAppLog(serial, h.update_id)
      .then((records) => setAppLogData(records))
      .catch((err) => setAppLogError(err.message || "Failed to load app log"))
      .finally(() => setAppLogLoading(false));
    // Metadata only - the log text of whichever phase is opened is fetched by
    // the panel itself, so selecting an update never pulls both files.
    getDeviceOtaLogs(serial, h.update_id)
      .then((records) => setOtaLogs(records))
      .catch((err) => setOtaLogsError(err.message || "Failed to load OTA logs"))
      .finally(() => setOtaLogsLoading(false));
  }
  // TC-0031 / WP-FL-04: arriving with ?tab=audit (the Failed/Rollback row's Audit
  // action) opens the device's audit trail directly - its most recent failed or
  // rolled-back update, else its most recent update - instead of an empty tab
  // asking for a click. Once per device; a normal visit (no tab) is unchanged.
  const autoAuditFor = useRef(null);
  useEffect(() => {
    if (tab !== "audit" || selectedHistory || !Array.isArray(fwHistory) || autoAuditFor.current === serial) return;
    autoAuditFor.current = serial;
    const pick = fwHistory.find((h) => h.update_id && (h.outcome === "FAIL" || h.outcome === "ROLLBACK"))
      || fwHistory.find((h) => h.update_id);
    if (pick) handleHistoryClick(pick, { view: "audit" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fwHistory, selectedHistory, serial]);

  if (loading) return <PageLoader />;

  // No mock fallback: a serial the service does not know is shown as not found,
  // never as a fabricated device record (DISC-015/026, TP-FMP-04-TS-01-TC-0013).
  if (deviceNotFound || !device) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <button
            onClick={() => navigate("/devices")}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors"
          >
            &#8592; Fleet Overview
          </button>
        </div>
        <div role="alert" className="bg-white border border-amber-200 rounded-xl px-6 py-8 text-center">
          <p className="text-lg font-semibold text-slate-900">Device not found</p>
          <p className="text-sm text-slate-500 mt-1">
            No device with serial number <span className="font-mono">{serial}</span> is registered in the fleet.
          </p>
        </div>
      </div>
    );
  }

  const statusCfg = {
    UP_TO_DATE:      { label: "Up to Date",       pill: "border border-emerald-400 text-emerald-600 bg-emerald-50" },
    MANDATORY:       { label: "Mandatory",         pill: "border border-red-300 text-red-600 bg-red-50" },
    PENDING:         { label: "Pending",           pill: "border border-amber-300 text-amber-600 bg-amber-50" },
    FAILED_ROLLBACK: { label: "Failed - Rollback", pill: "border border-red-300 text-red-600 bg-red-50" },
    UNKNOWN:         { label: "Unknown",           pill: "border border-slate-300 text-slate-500 bg-slate-50" },
  }[device.status] || { label: device.status, pill: "border border-slate-300 text-slate-500" };

  return (
    <div className="flex flex-col gap-0">
      {/* Page title row */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/devices")}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors shrink-0 mt-1"
          >
            &#8592; Fleet Overview
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Device {serial}</h1>
          </div>
        </div>
        {/* A stale status is flagged in its place, never shown as current (FMP-AN-009). */}
        {device.stale ? (
          <span className="mt-1"><StaleBadge device={device} lastKnown={statusCfg.label} size="lg" /></span>
        ) : (
          <span className={`text-sm font-semibold px-4 py-1.5 rounded-full mt-1 ${statusCfg.pill}`}>
            {statusCfg.label}
          </span>
        )}
      </div>

      {/* Header bar */}
      <div className="bg-[#08148C] rounded-xl px-6 py-4 mb-5">
        <div className="grid grid-cols-3 gap-6">
          {[
            { label: "SERIAL NO.",   value: serial,                                                                    highlight: false },
            // The device record's current firmware - not the newest entry of the history list, which follows the year/quarter filter.
            { label: "CURRENT FW",  value: device.currentFw || "-",                                                   highlight: true  },
            // The device's own last-sync time, as on the fleet list (NEW-K) - not the newest firmware-history event.
            { label: "LAST UPDATED", value: formatLastSync(device.lastUpdated), highlight: false },
          ].map(({ label, value, highlight }) => (
            <div key={label}>
              <p className="text-[11px] font-semibold text-blue-300 uppercase tracking-wider mb-1">{label}</p>
              <p className={`text-base font-bold ${highlight ? "text-blue-200" : "text-white"}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Body: two-column */}
      <div className="flex gap-5">
        {/* Left: FW History */}
        <div className="w-52 shrink-0 bg-white border border-slate-200 rounded-xl px-4 py-5">
          <p className="text-[11px] font-bold text-[#08148C] uppercase tracking-widest mb-3">FW History</p>
          {/* Year / Quarter filters */}
          <div className="flex flex-col gap-2 mb-4">
            <select
              value={fwYear}
              onChange={(e) => setFwYear(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={fwQuarter}
              onChange={(e) => setFwQuarter(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
            >
              <option value="">All Quarters</option>
              <option value="1">Q1 (Jan-Mar)</option>
              <option value="2">Q2 (Apr-Jun)</option>
              <option value="3">Q3 (Jul-Sep)</option>
              <option value="4">Q4 (Oct-Dec)</option>
            </select>
            <button
              onClick={() => {
                setFwFilterLoading(true);
                setFwHistory(null);
                setFwHistoryError(null);
                setSelectedHistory(null);
                getDeviceFirmwareHistory(serial, { year: fwYear || undefined, quarter: fwQuarter || undefined })
                  .then((records) => setFwHistory(records))
                  .catch((err) => setFwHistoryError(err.message || "Failed to load firmware history"))
                  .finally(() => setFwFilterLoading(false));
              }}
              className="text-xs font-semibold bg-[#08148C] text-white rounded-lg px-3 py-1.5 hover:bg-[#0d1fa8] transition-colors"
            >
              Apply Filter
            </button>
          </div>
          <div className="max-h-[58vh] overflow-y-auto pr-1">
            {/* Root Node: Serial Number. A real <button> rather than a div with
                role="button" — the latter satisfies S6848 but then trips S6819,
                and the only child here is a span, so the native tag is valid. */}
            <button
              type="button"
              aria-pressed={tab === "metadata" && !selectedHistory}
              onClick={() => {
                setSelectedHistory(null);
                setTab("metadata");
              }}
              className={`w-full text-left flex gap-2 items-center cursor-pointer rounded-lg px-2.5 py-2 mb-3 border transition-colors ${
                tab === "metadata" && !selectedHistory
                  ? "bg-blue-50 border-blue-200 text-[#08148C]"
                  : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <span className="text-xs font-bold whitespace-nowrap overflow-hidden text-ellipsis">📟 Device: {serial}</span>
            </button>

            {fwHistoryError && <p className="text-xs text-red-500">{fwHistoryError}</p>}
            {!fwHistoryError && (fwHistory === null || fwFilterLoading) && (
              <p className="text-xs text-slate-400">Loading...</p>
            )}
            {!fwHistoryError && fwHistory !== null && !fwFilterLoading && fwHistory.length === 0 && (
              <p className="text-xs text-slate-400">No history available.</p>
            )}
            {!fwHistoryError && fwHistory !== null && !fwFilterLoading && fwHistory.length > 0 && (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[7px] top-3 bottom-3 w-px bg-slate-200" />
              <div className="space-y-1">
                {fwHistory.map((h, i) => (
                  <button
                    key={i}  // NOSONAR S6479: raw backend rows carry no guaranteed unique id.
                    type="button"
                    // Entries with no update_id have no audit trail to open —
                    // handleHistoryClick already early-returns on them, so
                    // `disabled` is equivalent and keeps them out of the tab order.
                    disabled={!h.update_id}
                    aria-pressed={selectedHistory?.update_id === h.update_id && !!h.update_id}
                    onClick={() => handleHistoryClick(h)}
                    className={`w-full text-left flex gap-3 items-start relative rounded-lg px-1 py-1 -mx-1 transition-colors ${
                      h.update_id ? "cursor-pointer hover:bg-slate-50" : "opacity-60"
                    } ${
                      selectedHistory?.update_id === h.update_id && h.update_id ? "bg-blue-50" : ""
                    }`}
                  >
                    <span className={`block w-3.5 h-3.5 rounded-full mt-0.5 shrink-0 z-10 ${DOT_COLOR[h.outcome] || "bg-slate-300"}`} />
                    <span className="block">
                      <span className="block text-sm font-bold text-slate-800">{h.version}</span>
                      <span className="block text-xs text-slate-400 mb-1">{h.date}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${OUTCOME_PILL[h.outcome] || OUTCOME_PILL_DEFAULT}`}>{h.outcome}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Right: Tabs */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-slate-100 px-5 pt-3 gap-6">
            {TABS.map((t) => (
              <TabButton
                key={t.key}
                label={t.label}
                active={tab === t.key}
                onClick={() => {
                  // Device Info is not about one update, so leaving the
                  // per-update tabs clears the selection as it always has.
                  if (t.key === "metadata") setSelectedHistory(null);
                  setTab(t.key);
                }}
              />
            ))}
          </div>

          {/* Log table */}
          <div className="px-5 pb-5">
            {tab === "metadata" && (
              <div className="pt-5">
                <h3 className="text-base font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">📟 Device Metadata</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-slate-50/50 p-5 rounded-xl border border-slate-100">
                  {[
                    { label: "Serial Number", value: device.serial || "—", mono: true },
                    { label: "Model", value: device.model || "—" },
                    { label: "Current Firmware", value: device.currentFw || "—", fw: true },
                    { label: "Required Firmware", value: device.requiredFw || "—", fw: true },
                    // FMP-DV-001: the device's rollout segment, worded as on the fleet list (DISC-003).
                    { label: "Segment", value: formatSegment(device.segment).text },
                    { label: "Is Mandatory", value: device.isMandatory ? "Yes" : "No" },
                    { label: "Last Updated", value: formatLastSync(device.lastUpdated) },
                    { label: "Aging Days", value: device.agingDays != null ? `${device.agingDays} days` : "—" },
                    { label: "Status", value: device.status || "—", statusPill: true },
                    { label: "Current Country", value: device.currentCountry || "—" },
                    { label: "Base Country", value: device.baseCountry || "—" },
                    { label: "User Name", value: device.name || "—" },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{item.label}</span>
                      {item.statusPill ? (
                        <div className="flex">
                          {device.stale ? (
                            <StaleBadge device={device} lastKnown={statusCfg.label} />
                          ) : (
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusCfg.pill}`}>
                              {statusCfg.label}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={`text-sm font-medium ${metaTextClass(item)}`}>
                          {item.value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === "applog" && (
            <div className="pt-2">
              {!selectedHistory ? (
                <p className="text-xs text-slate-400 py-6 text-center">Click a FW History item on the left to view its app log.</p>
              ) : (
                <>
                  {appLogLoading && <PageLoader />}
                  {appLogError && <p className="text-xs text-red-500 py-4">{appLogError}</p>}
                  {!appLogLoading && !appLogError && appLogData !== null && (
                    appLogData.length === 0
                      ? <p className="text-xs text-slate-400 py-6 text-center">No app log entries found.</p>
                      : <div className="space-y-3">
                          {appLogData.map((row, i) => (
                            <div key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="border border-slate-200 rounded-lg p-4 bg-white">
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                                {[
                                  { label: "DEVICE / BASELINE", value: row.device_baseline || "�" },
                                  { label: "SERIAL NUMBER", value: row.serial_number || serial || "-", mono: true },
                                  { label: "FW VERSION", value: row.fw_version || "-", fw: true },
                                  { label: "PLATFORM", value: row.type || row.device || row.device_type || "�" },
                                  { label: "CHANNEL NAME", value: row.client_name || row.clientName || "�" },
                                  { label: "EVENT TYPE", value: row.event_type || "-", uppercase: true },
                                  { label: "UPLOADED TIME UTC", value: formatLogDateTime(row.uploaded_at || row.uploadedAt) },
                                  { label: "RECEIVED TIME UTC", value: formatLogDateTime(row.received_time || row.receivedTime) },
                                  { label: "SESSION ID", value: row.update_id || row.updateId || "�", mono: true },
                                  { label: "CLIENT NAME", value: row.actor || "-" },
                                  { label: "ROLE", value: row.role || "-" },
                                ].map((item) => (
                                  <div key={item.label}>
                                    <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-1">{item.label}</p>
                                    <p className={`text-base break-all ${logFieldTextClass(item)}`}>
                                      {item.value}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-4 pt-3 border-t border-slate-100">
                                {row.download_url
                                  ? <button
                                      onClick={() => {
                                        if (!canDownload) {
                                          setAccessDeniedOpen(true);
                                          return;
                                        }
                                        downloadLogAsPdf({
                                          downloadUrl: row.download_url,
                                          title: "Device App Log",
                                          subtitle: `Serial: ${row.serial_number || serial || "-"}   Update ID: ${row.update_id || "-"}   FW: ${row.fw_version || "-"}`,
                                          fileName: `${serial}_app-log.pdf`,
                                        }).catch(() => alert("Could not fetch log file. The download link may have expired."));
                                      }}
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#08148C] text-[#08148C] rounded px-3 py-1.5 hover:bg-[#08148C] hover:text-white transition-colors"
                                    >
                                      &#8659; Download Log
                                    </button>
                                  : <span className="text-slate-300 text-xs">�</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                  )}
                </>
              )}
            </div>
            )}
            {tab === "audit" && (
              <AuditTabPanel
                selectedHistory={selectedHistory}
                auditLoading={auditLoading}
                auditError={auditError}
                auditLog={auditLog}
                serial={serial}
                canDownload={canDownload}
                setAccessDeniedOpen={setAccessDeniedOpen}
                noUpdates={Array.isArray(fwHistory) && !fwHistory.some((h) => h.update_id)}
              />
            )}
            {tab === "otalog" && (
              <OtaLogTabPanel
                serial={serial}
                selectedHistory={selectedHistory}
                otaLogs={otaLogs}
                otaLogsLoading={otaLogsLoading}
                otaLogsError={otaLogsError}
                canDownload={canDownload}
                setAccessDeniedOpen={setAccessDeniedOpen}
                noUpdates={Array.isArray(fwHistory) && !fwHistory.some((h) => h.update_id)}
              />
            )}
          </div>
        </div>
      </div>
      <AccessDeniedDialog
        open={accessDeniedOpen}
        onOpenChange={setAccessDeniedOpen}
        message="Your role does not have permission to download logs."
      />
    </div>
  );
}


/**
 * Audit-trail tab. Split out of DeviceDetailPage so its load/empty/error
 * branches are scored separately (S3776).
 */
function AuditTabPanel({
  selectedHistory,
  auditLoading,
  auditError,
  auditLog,
  serial,
  canDownload,
  setAccessDeniedOpen,
  noUpdates,
}) {
  return (
            <div className="pt-2">
              {!selectedHistory && (
                <p className="text-xs text-slate-400 py-6 text-center">
                  {noUpdates
                    ? "No firmware update is recorded for this device, so there is no audit trail to show."
                    : "Click a FW History item on the left to view its audit trail."}
                </p>
              )}
              {selectedHistory && (
                <>

                  {auditLoading && <PageLoader />}
                  {auditError && <p className="text-xs text-red-500 py-4">{auditError}</p>}
                  {!auditLoading && !auditError && auditLog !== null && (
                    auditLog.length === 0
                      ? <p className="text-xs text-slate-400 py-6 text-center">No audit entries found.</p>
                      : <div className="space-y-3">
                          {auditLog.map((row, i) => (
                            <div key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="border border-slate-200 rounded-lg p-4 bg-white">
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                                {[
                                  { label: "DEVICE / BASELINE", value: row.device_baseline || "�" },
                                  { label: "SERIAL NUMBER", value: row.serial_number || serial || "-", mono: true },
                                  { label: "FW VERSION", value: row.fw_version || "-", fw: true },
                                  { label: "CHANNEL NAME", value: row.client_name || row.clientName || "�" },
                                  { label: "PLATFORM", value: row.type || row.device || row.device_type || "�" },
                                  { label: "EVENT TYPE", value: row.event_type || "-", uppercase: true },
                                  { label: "UPLOADED TIME UTC", value: formatLogDateTime(row.uploaded_at || row.uploadedAt) },
                                  { label: "RECEIVED TIME UTC", value: formatLogDateTime(row.received_time || row.receivedTime) },
                                  { label: "SESSION ID", value: row.update_id || row.updateId || "�", mono: true },
                                  { label: "CLIENT NAME", value: row.actor || "-" },
                                  { label: "ROLE", value: row.role || "-" },
                                ].map((item) => (
                                  <div key={item.label}>
                                    <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-1">{item.label}</p>
                                    <p className={`text-base break-all ${logFieldTextClass(item)}`}>
                                      {item.value}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-4 pt-3 border-t border-slate-100">
                                {row.update_id
                                  ? <button
                                      onClick={() => {
                                        if (!canDownload) {
                                          setAccessDeniedOpen(true);
                                          return;
                                        }
                                        downloadLogAsPdf({
                                          downloadUrl: row.download_url,
                                          title: "Device Audit Log",
                                          subtitle: `Serial: ${row.serial_number || "-"}   Update ID: ${row.update_id || "-"}   FW: ${row.fw_version || "-"}`,
                                          fileName: `${serial}_audit-log.pdf`,
                                        }).catch(() => alert("Could not fetch log file. The download link may have expired."));
                                      }}
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#08148C] text-[#08148C] rounded px-3 py-1.5 hover:bg-[#08148C] hover:text-white transition-colors"
                                    >
                                      &#8659; Download Log
                                    </button>
                                  : <span className="text-slate-300 text-xs">�</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                  )}
                </>
              )}
            </div>
  );
}